"use client";

import { getUniqueNumber } from "@highway/mbs-utils";
import {
    ComponentType,
    createContext,
    forwardRef,
    ReactNode,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { ISticky, useStickyAtom } from "../Sticky";
import { InputValidation } from "./types";

type TInputID = string | number;
type TInputRef = RefObject<Element | null>;

interface ValidationContextType {
    isChecking: boolean;
    validTags: string[];
    setInvalid: (key: string) => void;
    checkValid: (tags?: string[], scroll?: boolean) => boolean;
    getIsValid: (tags?: string[]) => boolean;
    setValidation: (key: TInputID, isValid: boolean) => void;
    setRef: (key: TInputID, ref: TInputRef | null) => void;
    stopValidation: () => void;
}

export const ValidationContext = createContext<ValidationContextType>(null as any);

export const useFormValidation = () => useContext(ValidationContext);

export const Validation = ({ children }: { children: ReactNode }) => {
    const { stickies } = useStickyAtom();
    const validMap = useRef({} as Record<TInputID, boolean>);
    const refsMap = useRef({} as Record<TInputID, TInputRef | null>);
    const [isChecking, setIsChecking] = useState(false);
    const [validTags, setValidTags] = useState<string[]>([]);

    const getIsValid = (tags: string[] = []) => {
        setValidTags(tags);
        const keysToCheck = tags.length > 0 ? tags : Object.keys(validMap.current);
        return keysToCheck.every((key) => validMap.current[key]);
    };

    const checkValid = (tags: string[] = [], scroll: boolean = true) => {
        setIsChecking(true);
        const isValid = getIsValid(tags);
        if (!isValid && scroll) scrollToFirstInvalidInput(validMap, refsMap, stickies);
        return isValid;
    };

    const setValidation = (key: TInputID, isValid: boolean) => {
        validMap.current[key] = isValid;
    };

    const setRef = (key: TInputID, ref: TInputRef | null) => {
        refsMap.current[key] = ref;
    };

    const stopValidation = () => {
        setIsChecking(false);
    };

    const setInvalid = (key: string) => {
        validMap.current[key] = false;
        setIsChecking(true);
    };

    const value = useMemo(
        () => ({
            isChecking,
            checkValid,
            setValidation,
            setRef,
            stopValidation,
            validTags,
            setInvalid,
            getIsValid,
        }),
        [isChecking, validTags, checkValid],
    );
    return <ValidationContext.Provider value={value}>{children}</ValidationContext.Provider>;
};

export function withValidation<T extends object>(Component: ComponentType<T>) {
    const component = forwardRef<unknown, T>((props, ref) => (
        <Validation>
            <Component {...(props as T)} forwardedRef={ref} />
        </Validation>
    ));
    component.displayName = "Validation";
    return component;
}

export const useValidation = ({
    value,
    isFocused = false,
    validation,
    tag = "",
    ref,
}: {
    value: TInputID | Date | undefined | string[];
    isFocused?: boolean;
    validation?: InputValidation | InputValidation[];
    tag?: string;
    ref?: RefObject<HTMLElement | null>;
}) => {
    const uniqueID = useRef(tag || getUniqueNumber());
    const validationContext = useContext(ValidationContext);
    const [values, setValues] = useState({ isValid: true, message: "" });

    useEffect(() => {
        if (ref && validationContext) validationContext.setRef(uniqueID.current, ref);
        return () => {
            if (validation && validationContext) {
                validationContext.setRef(uniqueID.current, null);
                validationContext.setValidation(uniqueID.current, true);
            }
        };
    }, []);

    const runValidation = useCallback(
        (validation: InputValidation) => {
            const validationValue = validation(value);
            setValues(validationValue);
            validationContext?.setValidation(uniqueID.current, validationValue.isValid);
            return validationValue;
        },
        [value, validationContext],
    );

    useEffect(() => {
        if (validation && validationContext && !isFocused) {
            if (Array.isArray(validation)) {
                validation.some((v) => {
                    const validationValue = runValidation(v);
                    return !validationValue.isValid;
                });
            } else runValidation(validation);
        } else if (!validation && !values.isValid) {
            setValues({ isValid: true, message: "" });
            validationContext?.setValidation(uniqueID.current, true);
        }
    }, [validation, value, isFocused]);

    const hasError = () => {
        const { validTags, isChecking } = validationContext || {};
        return isChecking && !values.isValid && (!validTags?.length || validTags.includes(tag));
    };

    return {
        error: hasError(),
        errorMessage: values.message,
    };
};

export const scrollToFirstInvalidInput = (
    validMap: RefObject<Record<TInputID, boolean>>,
    refsMap: RefObject<Record<TInputID, TInputRef | null>>,
    stickies: ISticky[],
) => {
    const invalidElements = Object.keys(validMap.current)
        .filter((key) => !validMap.current[key] && refsMap.current[key])
        .map((key) => refsMap.current[key]?.current)
        .filter((element): element is HTMLElement => !!element);

    if (invalidElements.length === 0) return;

    const topmostInvalidRef = invalidElements.reduce((topmost, element) => {
        const topmostTop = topmost.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;
        return elementTop < topmostTop ? element : topmost;
    });

    const rect = topmostInvalidRef.getBoundingClientRect();
    const stickiesHeight = stickies.reduce(
        (obj, sticky) => ({
            top: obj.top + (sticky.position === "top" ? sticky.height : 0),
            bottom: obj.bottom + (sticky.position === "bottom" ? sticky.height : 0),
        }),
        { top: 0, bottom: 0 },
    );

    // Only scroll if element is not visible. This includes stickies element overlap
    const isVisible =
        rect.top >= stickiesHeight.top && rect.bottom <= window.innerHeight - stickiesHeight.bottom;
    if (!isVisible) {
        // If within a presentation element (modal/drawer/dialog/popover...), stickies can be ignored
        if (topmostInvalidRef.closest('[role="presentation"]')) {
            topmostInvalidRef.scrollIntoView({ behavior: "smooth" });
        } else {
            window.scrollTo({
                top: window.scrollY + rect.top - stickiesHeight.top,
                behavior: "smooth",
            });
        }
    }
};
